import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, Check, Download } from 'lucide-react';
import api from '../api/axios';
import Sqids from 'sqids';
import { generateCompliantPassword, validatePassword, passwordPolicyHint } from '../utils/passwordPolicy';
import { escapeCsvCell } from '../utils/csv';

const TimeZoneList = ({ selectedTimeZone, onChange }) => {
  // You can use hardcoded arrays or full list from Intl
  const timezones = Intl.supportedValuesOf("timeZone");

  // Divide into regions
  const regions = {
    Africa: timezones.filter((tz) => tz.startsWith("Africa/")),
    America: timezones.filter((tz) => tz.startsWith("America/")),
    Asia: timezones.filter((tz) => tz.startsWith("Asia/")),
  };

  // Function to get correct UTC offset
  const getOffset = (timeZone) => {
    const now = new Date();
    
    // Get the local time string in the target timezone
    const tzString = now.toLocaleString("en-US", { 
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
    
    // Get the local time string in UTC
    const utcString = now.toLocaleString("en-US", { 
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
    
    // Both interpreted as local time, so the difference is the offset
    const tzDate = new Date(tzString);
    const utcDate = new Date(utcString);
    
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const minutes = String(absMinutes % 60).padStart(2, "0");
    
    return `${sign}${hours}:${minutes}`;
  };
  // Render options for a region
  // const renderRegionOptions = (regionName, tzArray) => (
  //   <>
  //     <option disabled style={{ fontWeight: "bold" }}>{regionName}</option>
  //     {tzArray.map((tz) => (
  //       <option key={tz} value={tz}>
  //         {getOffset(tz)} {tz}
  //       </option>
  //     ))}
  //   </>
  // );

  const renderRegionOptions = (regionName, tzArray) => {
    // Return an array instead of a fragment
    const options = [
      <option key={`${regionName}-header`} disabled style={{ fontWeight: "bold" }}>
        {regionName}
      </option>
    ];
    
    tzArray.forEach((tz) => {
      options.push(
        <option key={tz} value={tz}>
          {getOffset(tz)} {tz}
        </option>
      );
    });
    
    return options;
  };


  return (
    <select className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md" value={selectedTimeZone} onChange={(e) => onChange(e.target.value)}>
      <option value="UTC">+00:00 UTC</option>
      {renderRegionOptions("Africa", regions.Africa)}
      {renderRegionOptions("America", regions.America)}
      {renderRegionOptions("Asia", regions.Asia)}
    </select>
  );
};


const AdminManagement = () => {
  const [currentView, setCurrentView] = useState('list');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [admins, setAdmins] = useState([]);

  const sqids = new Sqids({
  alphabet: 'abcdefghijklmnopqrstuvwxyz@#%$&*0123456789',
  minLength: 10
});

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    userpk: null,
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    timezone: 'UTC',
    active: 'Y',
    accountExpires: 'N',
    expiryDate: ''
  });

  // Fetch all administrators on component mount
  useEffect(() => {
    fetchAdmins();
  }, [currentView]);

  // API call to fetch all administrators
  const fetchAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/webapi/admin");
      
      const data = await response.data;
      setAdmins(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  // API call to create new administrator
  const createAdmin = async (adminData) => {
    setLoading(true);
    setError(null);
    try {
      await api.post("/webapi/admin",adminData);
      
      // if (!response.ok) {
      //   const errorData = await response.json();
      //   throw new Error(errorData.error || 'Failed to create administrator');
      // }
      
      // const result = await response.json();
      // await fetchAdmins(); // Refresh the list
      // return result;
      setSuccessMessage('Add successful!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add.');
      console.error('Error creating admin:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // API call to update administrator
  const updateAdmin = async (adminData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put("/webapi/admin", adminData);
      
      // if (!response.ok) {
      //   const errorData = await response.json();
      //   throw new Error(errorData.error || 'Failed to update administrator');
      // }
      setSuccessMessage('Update successful!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update.');
      console.error('Error updating admin:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // API call to delete administrator
  // const deleteAdmin = async (userpk) => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const response = await fetch(API_BASE_URL, {
  //       method: 'DELETE',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ userpk }),
  //     });
      
  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.error || 'Failed to delete administrator');
  //     }
      
  //     const result = await response.json();
  //     await fetchAdmins(); // Refresh the list
  //     return result;
  //   } catch (err) {
  //     setError(err.message);
  //     console.error('Error deleting admin:', err);
  //     throw err;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAdmins = () => {
    if (!sortConfig.key) return admins;
    
    return [...admins].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleNewAdmin = () => {
    setSuccessMessage(null);
    setSelectedAdmin(null);
    setFormData({
      userpk: null,
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      email: '',
      timezone: 'UTC',
      active: 'Y',
      accountExpires: 'N',
      expiryDate: '',
      userLocked: 0
    });
    setError(null);
    setCurrentView('edit');
  };

  const handleEditAdmin = (admin) => {
    setSuccessMessage(null);
    setSelectedAdmin(admin);
    setFormData({
      userpk: admin.userpk,
      username: admin.username,
      password: '',
      confirmPassword: '',
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      timezone: admin.timezone,
      active: admin.status,
      accountExpires: admin.accountExpires,
      expiryDate: admin.expiryDate || '',
      userLocked: admin.userLocked ?? 0
    });
    setError(null);
    setCurrentView('edit');
  };

  const handleSave = async () => {
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    if (!selectedAdmin && !formData.username) {
      setError('Username is required');
      return;
    }

    if (!selectedAdmin && !formData.password) {
      setError('Password is required for new administrator');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password) {
      const policyError = validatePassword(formData.password);
      if (policyError) {
        setError(policyError);
        return;
      }
    }

    try {
      const adminData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        timezone: formData.timezone,
        active: formData.active,
        accountExpires: formData.accountExpires,
        expiryDate: formData.expiryDate,
        userLocked: formData.userLocked
      };

      if (formData.password) {
        adminData.password = formData.password;
      }
      if (formData.confirmPassword) {
        adminData.confirmPassword = formData.confirmPassword;
      }
      if (selectedAdmin) {
        // Update existing admin
        adminData.userpk = formData.userpk;
        await updateAdmin(adminData);
      } else {
        // Create new admin
        adminData.username = formData.username;
        adminData.password = formData.password;
        await createAdmin(adminData);
      }
      
      //setCurrentView('list');
      setError(null);
    } catch (err) {
      // Error is already set by the API call
    }
  };

  const handleGeneratePassword = () => {
    const generatedPassword = generateCompliantPassword();
    setFormData(prev => ({
      ...prev,
      password: generatedPassword,
      confirmPassword: generatedPassword
    }));
    setError(null);
  };

  const handleDownloadCSV = () => {
    if (!admins || admins.length === 0) {
      setError('No users to download.');
      return;
    }

    const headers = ['Username', 'Name', 'Email', 'Last Login', 'Status'];
    const csvContent = [
      headers.join(','),
      ...getSortedAdmins().map(admin => {
        return [
          escapeCsvCell(admin.username),
          escapeCsvCell(admin.name),
          escapeCsvCell(admin.email),
          escapeCsvCell(admin.lastLogin),
          escapeCsvCell(admin.status === 'Y' ? 'Active' : 'Inactive')
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `administrators_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // const handleDelete = async (admin) => {
  //   if (window.confirm(`Are you sure you want to delete administrator "${admin.username}"?`)) {
  //     try {
  //       await deleteAdmin(admin.userpk);
  //     } catch (err) {
  //       // Error is already set by the API call
  //     }
  //   }
  // };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronDown className="w-4 h-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-indigo-600" />
      : <ChevronDown className="w-4 h-4 text-indigo-600" />;
  };

  if (currentView === 'edit') {
    return (
      <div className="min-h-screen ">
        <div className="max-w-5xl ">
          <h1 className="text-3xl font-semibold text-gray-800 mb-2">Administrator</h1>
          <div className="flex gap-4 mb-8">
            <button 
              onClick={() => setCurrentView('list')}
              className="text-indigo-600 hover:text-indigo-700"
            >
              Administrator list
            </button>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">User Record</span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
          {loading && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-6">
              Processing...
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Login Access</h2>
            
            <div className="mb-6">
              <label className="block text-sm text-gray-600 mb-2">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                autoComplete="off"
                className="w-full max-w-lg px-4 py-2 bg-gray-100 border border-gray-300 rounded"
                disabled={selectedAdmin !== null}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 max-w-lg">
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="block text-sm text-gray-600">Password</label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    autoComplete="off"
                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    autoComplete="off"
                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              {passwordPolicyHint}
            </p>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData?.active === 'Y'}
                  onChange={(e) => setFormData({...formData, active: e.target.checked ? 'Y' : 'N' })}
                  className="w-5 h-5 border-2 border-gray-300 rounded appearance-none checked:bg-indigo-600 checked:border-indigo-600 cursor-pointer"
                />
                {formData.active === 'Y' && (
                  <Check className="w-4 h-4 text-white absolute top-0.5 left-0.5 pointer-events-none" />
                )}
              </div>
              <span className="text-gray-700">Active - Account status</span>
            </label>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData?.userLocked === 1}
                  onChange={(e) => setFormData({...formData, userLocked: e.target.checked ? 1 : 0 })}
                  className="w-5 h-5 border-2 border-gray-300 rounded appearance-none checked:bg-red-600 checked:border-red-600 cursor-pointer"
                />
                {formData.userLocked === 1 && (
                  <Check className="w-4 h-4 text-white absolute top-0.5 left-0.5 pointer-events-none" />
                )}
              </div>
              <span className="text-gray-700">Locked - Account locked</span>
            </label>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData?.accountExpires === 'Y'}
                    onChange={(e) => setFormData({...formData, accountExpires: e.target.checked ? 'Y' : 'N' })}
                    className="w-5 h-5 border-2 border-gray-300 rounded appearance-none checked:bg-indigo-600 checked:border-indigo-600 cursor-pointer"
                  />
                  {formData.accountExpires && (
                    <Check className="w-4 h-4 text-white absolute top-0.5 left-0.5 pointer-events-none" />
                  )}
                </div>
                <span className="text-gray-700">Account expires</span>
              </label>
              
              {formData.accountExpires && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                    placeholder="28/08/2024"
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Info and Preference</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm text-gray-600 mb-2">First name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  autoComplete="off"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Last name / surname</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  autoComplete="off"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm text-gray-600 mb-2">New email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm text-gray-600 mb-2">Time Zone</label>
              {/* <div className="relative max-w-lg">
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded appearance-none bg-white"
                >
                  <option>+08:00 Asia/Singapore</option>
                  <option>+00:00 UTC</option>
                  <option>-05:00 America/New_York</option>
                </select>
                <ChevronDown className="w-5 h-5 text-gray-400 absolute right-3 top-3 pointer-events-none" />
              </div> */}<div className="relative max-w-lg">
              <TimeZoneList
                  selectedTimeZone={formData.timezone}
                  onChange={(value) => setFormData({...formData, timezone: value})} // updates state automatically
                /></div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setCurrentView('list')}
                disabled={loading}
                className="px-6 py-2.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div >
        <h1 className="text-3xl font-semibold text-gray-800 mb-8">Administrator</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="text-sm text-gray-600">Registered Admin</div>
          <div className="text-3xl font-semibold text-gray-800">{admins.length}</div>
        </div>

        <div className="flex gap-4 mb-6">
          <button 
            onClick={handleNewAdmin}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
            Administrator
          </button>
          <button
            onClick={handleDownloadCSV}
            disabled={loading || !admins || admins.length === 0}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            CSV
          </button>
        </div>

        {loading && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-6">
            Loading administrators...
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer">
                    Username
                  </div>
                </th>
                <th className="px-6 py-4 text-left">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer">
                    Name
                  </div>
                </th>
                <th className="px-6 py-4 text-left">
                  <div 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800"
                    onClick={() => handleSort('email')}
                  >
                    E-mail
                    <SortIcon column="email" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left">
                  <div 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800"
                    onClick={() => handleSort('lastLogin')}
                  >
                    Last Login
                    <SortIcon column="lastLogin" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left">
                  <div 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800"
                    onClick={() => handleSort('status')}
                  >
                    STATUS
                    <SortIcon column="status" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left">
                  <div 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800"
                    
                  >
                    QR Code
                    
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedAdmins().map((admin, index) => (
                <tr 
                  key={index} 
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  
                >
                  <td onClick={() => handleEditAdmin(admin)} className="px-6 py-4 text-gray-800">{admin.username}</td>
                  <td onClick={() => handleEditAdmin(admin)}  className="px-6 py-4 text-gray-800">{admin.name}</td>
                  <td onClick={() => handleEditAdmin(admin)}  className="px-6 py-4 text-gray-800">{admin.email}</td>
                  <td onClick={() => handleEditAdmin(admin)}  className="px-6 py-4 text-gray-800">{admin.lastLogin}</td>
                  <td className="px-6 py-4 text-gray-800">{admin?.status == "Y" ? "Active" : "Inactive"}</td>
                  <td className="px-6 py-4 text-gray-800"><a href={'/webapi/qrcode?Userpk='+encodeURIComponent(sqids.encode([admin?.userpk]))} target="_blank" rel="noopener noreferrer">view</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
